/**
 * Migrating between different versions of the design material parameters.
 * @module
 */
import { productLog } from "common/utils/logging";
import { Color } from "three";
import { defaultSdfPattern } from "./designMaterialDefaults";
import { NUM_LAYERS } from "./designMaterialDefaults";
import type { DesignMaterialMigration } from "./designMaterialTypes";
import { cloneDefaultMaterialParams } from "./designMaterialUtils";
import {
  legacyDefaultDesignMaterialParams,
  legacyDefaultLayerParams,
  legacyDefaultMaskParams,
  legacyDefaultSdfParams,
  legacyDefaultSdfPattern,
} from "./legacyMaterialParams";
import { textureDir } from "./materialEditorTextures";
import {
  REPEATING_TEXTURE_MIGRATION_SCALE_FACTOR,
  UpscaleMethod,
  getTextureNameFromPackedNameAndChannel,
  getTextureUpscaleMethod,
} from "./materialTextureMigration";

export const designMaterialMigrations: DesignMaterialMigration[] = [
  {
    maxSchemaVersion: "0.3.999",
    applyOnLayerConfigs: false,
    apply(_res: any) {
      productLog("Tried to open design material in format that's no longer supported.");
      return cloneDefaultMaterialParams();
    },
  },
  {
    maxSchemaVersion: "0.4.7",
    applyOnLayerConfigs: false,
    apply(res: any) {
      const updatedRes: any = {};

      // base properties
      Object.assign(updatedRes, legacyDefaultDesignMaterialParams);

      updatedRes.compatibleLayerConfigLibraries = [];
      updatedRes.compatibleColorLibraries = [];

      for (const key of Object.keys(legacyDefaultDesignMaterialParams)) {
        if (res[key] !== undefined) {
          updatedRes[key] = res[key];
        }
      }

      updatedRes.color = "#" + new Color(updatedRes.color).getHexString();

      if (res.generatesDefaultMaterial !== undefined) {
        updatedRes.allowUsingTemplateOnItsOwn = res.generatesDefaultMaterial;
      }

      // sdf properties
      updatedRes.sdf = { ...legacyDefaultSdfParams };
      for (const key of Object.keys(legacyDefaultSdfParams)) {
        if (res.sdf[key] !== undefined) {
          updatedRes.sdf[key] = res.sdf[key];
        }
      }

      updatedRes.sdf.patterns = [];
      for (let j = 0; j < 4; j++) {
        const outParams: any = { ...legacyDefaultSdfPattern };
        if (j === 0) {
          outParams.enabled = true;
        }
        updatedRes.sdf.patterns.push(outParams);

        const inParams = res.sdf.patterns[j];

        for (const key of Object.keys(outParams)) {
          if (inParams[key] !== undefined) {
            outParams[key] = inParams[key];
          }
        }

        if (inParams.roundSides) {
          outParams.roundSidesMode = "roundSides";
          outParams.roundStrength = inParams.roundSidesStrength;
        } else if (inParams.roundY) {
          outParams.roundSidesMode = "roundY";
          outParams.roundStrength = inParams.roundYStrength;
        } else if (inParams.roundX) {
          outParams.roundSidesMode = "roundX";
          outParams.roundStrength = inParams.roundXStrength;
        }
      }

      // layer properties
      updatedRes.layers = [];

      for (let j = 0; j < 4; j++) {
        const outParams: any = { ...legacyDefaultLayerParams };
        updatedRes.layers.push(outParams);

        if (j >= res.layers.length || res.layers[j].disabled) {
          outParams.enabled = false;
          continue;
        }

        const inParams = res.layers[j];

        for (const key of Object.keys(outParams)) {
          if (inParams[key] !== undefined) {
            outParams[key] = inParams[key];
          }
        }

        if (inParams.invert !== undefined) {
          outParams.invertInput = inParams.invert;
        }

        const layerHasTextureMask = outParams.maskID.startsWith("Mask");
        if (layerHasTextureMask) {
          const inMaskLayerIndex = parseInt(outParams.maskID.substring(4));
          if (inMaskLayerIndex >= NUM_LAYERS) {
            outParams.maskID = ("Mask" + (inMaskLayerIndex - NUM_LAYERS)) as "Mask0" | "Mask1" | "Mask2" | "Mask3";
          }
        }

        if (inParams.sdfRandomMaskMultiplier !== undefined) {
          outParams.sdfMaskRandomness = inParams.sdfRandomMaskMultiplier;
        } else if (inParams.maskID === "SDFMask") {
          outParams.maskID = "SDFMasks";
          outParams.sdfMaskRandomness = 0;
        } else if (inParams.maskID === "SDFRandom") {
          outParams.maskID = "SDFMasks";
          outParams.sdfMaskRandomness = 1;
        }
      }

      updatedRes.masks = [];

      for (let j = 0; j < 4; j++) {
        const outParams = { ...legacyDefaultMaskParams };
        updatedRes.masks.push(outParams);

        let inParams;
        if (j + 4 < res.layers.length) {
          inParams = res.layers[j + 4];
        } else if (res.masks !== undefined && j < res.masks.length) {
          inParams = res.masks[j];
        } else {
          continue;
        }

        for (const key of Object.keys(outParams)) {
          if (inParams[key] !== undefined) {
            outParams[key] = inParams[key];
          }
        }
      }

      updatedRes.version = "0.4.8";

      return updatedRes;
    },
  },
  {
    maxSchemaVersion: "0.4.8",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let j = 0; j < 4; j++) {
        res.layers[j].randomMaskUniqueness = 0;
      }
      res.version = "0.4.9";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.9",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let j = 0; j < 4; j++) {
        delete res.layers[j].maskRandomUvOffsetStrength;
      }
      res.version = "0.4.10";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.10",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.transmission = 0;
      res.version = "0.4.11";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.11",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.heightMapURL = "";
      res.heightMapScaleU = 50;
      res.heightMapScaleV = 50;
      res.heightMapDepth = 1;
      res.heightMapRotation = 0;
      res.layerHeightFilterWidth = res.heightMapFilterWidth;
      delete res.heightMapFilterWidth;
      res.version = "0.4.12";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.12",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.heightMapChannel = 0;
      res.version = "0.4.13";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.13",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      // Before this, the maps were enabled implicitly whenever the texture url was set to something other than empty string.
      // This just prevents these maps from being disabled by default.
      res.normalMapEnabled = true;
      res.heightMapEnabled = true;
      res.version = "0.4.14";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.14",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.thumbnailAoStrength = res.sdf.aoStrength;
      res.sdf.thumbnailAoTolerance = res.sdf.aoTolerance;
      res.version = "0.4.15";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.15",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.thumbnailDiagonalCamera = false;
      res.version = "0.4.16";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.16",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.autoGenerateHatchIfMaskOnly = false;
      res.version = "0.4.17";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.17",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.randomTilesMode = res.sdf.useRandomTiles ? "LegacyRandomTiles" : "None";
      delete res.sdf.useRandomTiles;
      res.version = "0.4.18";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.18",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.hatchMaterialIgnoreVaryHeight = true;
      res.version = "0.4.19";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.19",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.tileSplitDirection = "None";
      res.sdf.tileSplitProbability = 0;
      res.version = "0.4.20";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.20",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.slopeAxis = "X";
      res.version = "0.4.21";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.21",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.enableApproximateTransmission = true;
      res.version = "0.4.22";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.22",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      delete res.sdf.tileRotation;
      delete res.sdf.tileSizeX;
      delete res.sdf.tileSizeY;
      delete res.sdf.tileSizeZ;
      delete res.sdf.tileOffsetX;
      delete res.sdf.tileOffsetY;
      delete res.sdf.tileOffsetZ;
      delete res.sdf.tileRoundness;
      delete res.sdf.tileEdgeSize;
      delete res.sdf.tileVaryHeightStr;
      delete res.sdf.tileNumCuts;
      delete res.sdf.tileSeed;
      delete res.sdf.tileYCutFirst;
      delete res.sdf.tileXVariation;
      res.version = "0.4.23";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.23",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      while (res.sdf.patterns.length < 8) {
        res.sdf.patterns.push({ ...defaultSdfPattern });
      }
      res.version = "0.4.24";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.24",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      for (let i = 0; i < res.sdf.patterns.length; ++i) {
        res.sdf.patterns[i].maskRandomSeedIndex = i;
      }
      res.version = "0.4.25";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.25",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      const distortionParams = {
        enabled: false,
        periodX: 5,
        periodY: 5,
        strengthX: 2,
        strengthY: 2,
        strengthZ: 0.5,
        randomness: 0,
      };
      res.sdf.distortions = [{ ...distortionParams }, { ...distortionParams }, { ...distortionParams }];
      res.version = "0.4.26";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.26",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        if (res.layers[i].blendMode === "screen") {
          res.layers[i].blendMode = "legacy-screen";
        }
      }
      res.version = "0.4.27";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.27",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        res.layers[i].sdfMaskPatternMask = 255;
      }
      res.version = "0.4.28";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.28",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        res.layers[i].sdfMaskDepthOverlay = 0;
      }
      res.version = "0.4.29";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.29",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        res.layers[i].enableMaskThreshold = false;
        res.layers[i].maskThreshold = 0.5;
      }
      res.version = "0.4.30";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.30",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        if (res.layers[i].maskID === "SolidSDFMask") {
          res.layers[i].sdfMaskRandomness *= 0.5;
        }
        res.layers[i].sdfMaskRandomAverage = 0.5;
      }
      res.version = "0.4.31";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.31",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.minTileVariability = 0;
      res.sdf.alwaysSplitTilesAboveWidth = 100;
      res.sdf.alwaysSplitTilesAboveHeight = 100;
      res.sdf.minTileHeight = 0;
      res.sdf.minTileWidth = 0;
      res.version = "0.4.32";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.32",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        if (res.layers[i].maskID === "emulateVaryHeight") {
          res.layers[i].maskID = "SDFMask";
          res.layers[i].sdfMaskRandomAverage = 1;
          if (res.sdf !== undefined) {
            res.layers[i].sdfMaskRandomness *= 0.51 / Math.max(res.sdf.depth, 0.0001);
          } else {
            // All layer configs that used "emulateVaryHeight" are for brick, so we can get away with this hardcoded value.
            const brickDepth = 0.3;
            res.layers[i].sdfMaskRandomness *= 0.51 / brickDepth;
          }
          res.layers[i].sdfMaskRandomness = Math.min(res.layers[i].sdfMaskRandomness, 2);
        } else if (res.layers[i].maskID === "SDFMasks") {
          res.layers[i].maskID = "SDFMask";
          res.layers[i].sdfMaskRandomness = res.layers[i].sdfMaskRandomness * 0.25;
          res.layers[i].sdfMaskRandomAverage = 1 - res.layers[i].sdfMaskRandomness;
          res.layers[i].sdfMaskDepthOverlay = 1 - res.layers[i].sdfMaskRandomness;
        } else if (res.layers[i].maskID === "SolidSDFMask") {
          res.layers[i].maskID = "SDFMask";
        }
      }
      res.version = "0.4.33";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.33",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (let i = 0; i < res.layers.length; ++i) {
        res.layers[i].sdfMaskPatterns = res.layers[i].sdfMaskPatternMask;
        delete res.layers[i].sdfMaskPatternMask;
        if (res.layers[i].srcID === "SDFMask") {
          res.layers[i].srcID = "SDFDepthMask";
        }
      }
      res.version = "0.4.34";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.34",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      const baseNameRegex = /\/static\/textures\/(.*).png/;
      function processLayerOrMask(layer) {
        const baseName = layer.textureURL.match(baseNameRegex)[1];
        const newName = getTextureNameFromPackedNameAndChannel(baseName, layer.channel, true);
        const upscaleMethod = getTextureUpscaleMethod(baseName, layer.channel);
        if (upscaleMethod === UpscaleMethod.REPEAT) {
          layer.scaleU *= REPEATING_TEXTURE_MIGRATION_SCALE_FACTOR;
          layer.scaleV *= REPEATING_TEXTURE_MIGRATION_SCALE_FACTOR;
        }
        layer.textureName = `${newName}`;
        delete layer.textureURL;
        delete layer.channel;
        return upscaleMethod;
      }
      for (let i = 0; i < res.layers.length; ++i) {
        const layer = res.layers[i];
        if (layer.channel > 2) {
          layer.colorCount = 2;
          if (layer.invertInput) {
            layer.color1 = layer.color0;
          } else {
            layer.color0 = layer.color1;
          }
        }
        const upscaleMethod = processLayerOrMask(layer);
        if (upscaleMethod === UpscaleMethod.SCALE) {
          layer.heightInfluence *= 0.8;
        }
      }
      for (let i = 0; i < res.masks.length; ++i) {
        processLayerOrMask(res.masks[i]);
      }
      if (res.heightMapURL !== undefined) {
        if (res.heightMapURL !== "") {
          const baseName = res.heightMapURL.match(baseNameRegex)[1];
          const newName = getTextureNameFromPackedNameAndChannel(baseName, res.heightMapChannel, true);
          const upscaleMethod = getTextureUpscaleMethod(baseName, res.heightMapChannel);
          if (upscaleMethod === UpscaleMethod.REPEAT) {
            res.heightMapScaleU *= REPEATING_TEXTURE_MIGRATION_SCALE_FACTOR;
            res.heightMapScaleV *= REPEATING_TEXTURE_MIGRATION_SCALE_FACTOR;
          }
          res.heightMapName = newName;
          if (upscaleMethod === UpscaleMethod.SCALE) {
            res.heightMapDepth *= 0.8;
          }
        } else {
          res.heightMapName = "";
        }
        delete res.heightMapURL;
        delete res.heightMapChannel;
      }
      res.version = "0.4.35";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.35",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      const sdfCommonKeys: string[] = [
        "aoStrength",
        "aoTolerance",
        "autoGenerateHatchIfMaskOnly",
        "depth",
        "maskOnly",
        "randomMaskSeed",
        "randomTilesMode",
        "slopeAxis",
        "thumbnailAoStrength",
        "thumbnailAoTolerance",
        "useAO",
        "useShadows",
      ];
      res.sdf.common = {};
      for (const key of sdfCommonKeys) {
        res.sdf.common[key] = res.sdf[key];
        delete res.sdf[key];
      }
      const sdfRandomTilesKeys: string[] = [
        "alwaysSplitTilesAboveHeight",
        "alwaysSplitTilesAboveWidth",
        "minTileHeight",
        "minTileVariability",
        "minTileWidth",
        "tileSplitDirection",
        "tileSplitProbability",
        "tileVariability",
      ];
      res.sdf.randomTiles = {};
      for (const key of sdfRandomTilesKeys) {
        res.sdf.randomTiles[key] = res.sdf[key];
        delete res.sdf[key];
      }
      res.version = "0.4.36";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.36",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      function isSdfV0436ContinuousX(sdfCommon: any, pattern: any) {
        if (sdfCommon.randomTilesMode !== "None") {
          return false;
        }
        if (sdfCommon.slopeAxis === "Z" && pattern.slope !== 0) {
          return false;
        }
        const bevelDistanceY = pattern.useBevels ? pattern.bevelDistanceY : 0;
        const cutXfromYScale = pattern.cutXfromY ? pattern.cutXfromYScale : 0;
        return (
          pattern.xDistance <= pattern.xSize * (1 - cutXfromYScale) - bevelDistanceY &&
          (pattern.roundSidesMode === "none" || pattern.roundSidesMode === "roundX" || pattern.roundStrength === 0) &&
          (!pattern.varyHeight || pattern.varyHeightScale === 0)
        );
      }
      function isSdfV0436ContinuousY(sdfCommon: any, pattern: any) {
        if (sdfCommon.randomTilesMode !== "None") {
          return false;
        }
        const bevelDistanceX = pattern.useBevels ? pattern.bevelDistanceX : 0;
        return (
          pattern.yDistance <= pattern.ySize - bevelDistanceX &&
          (pattern.roundSidesMode === "none" || pattern.roundSidesMode === "roundY" || pattern.roundStrength === 0) &&
          pattern.slope === 0 &&
          (!pattern.cutXfromY || pattern.cutXfromYScale === 0) &&
          (!pattern.varyHeight || pattern.varyHeightScale === 0)
        );
      }
      for (const pattern of res.sdf.patterns) {
        pattern.continuousX = isSdfV0436ContinuousX(res.sdf.common, pattern);
        pattern.continuousY = isSdfV0436ContinuousY(res.sdf.common, pattern);
      }
      res.version = "0.4.37";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.37",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.mainMaskTexName = res.mainMaskTexURL;
      res.normalMapName = res.normalMapURL;
      // The "mainMaskTexURL" was in a semi-broken state before this change. It isn't consistent with regards to using file extensions.
      // It's an experimental feature that's currently unused by our material set.
      if (res.mainMaskTexName.startsWith(textureDir)) {
        res.mainMaskTexName = (res.mainMaskTexName as string).substring(textureDir.length);
      }
      const pngExt = ".png";
      if (res.normalMapName.endsWith(pngExt)) {
        res.normalMapName = (res.normalMapName as string).substring(0, res.normalMapName.length - pngExt.length);
      }
      if (res.normalMapName.startsWith(textureDir)) {
        res.normalMapName = (res.normalMapName as string).substring(textureDir.length);
      }
      delete res.mainMaskTexURL;
      delete res.normalMapURL;
      res.version = "0.4.38";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.38",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.common.transparent = false;
      res.version = "0.4.39";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.39",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      let hasSmallBevels = false;
      let hasLargeBevels = false;
      for (const pattern of res.sdf.patterns) {
        if (pattern.enabled && pattern.useBevels) {
          if (pattern.bevelDistanceZ > 0 && pattern.bevelDistanceZ <= 0.2) {
            hasSmallBevels = true;
          }
          if (pattern.bevelDistanceX > 0.2 || pattern.bevelDistanceY > 0.2 || pattern.bevelDistanceZ > 0.2) {
            hasLargeBevels = true;
          }
        }
      }
      res.hatchMaterialIgnoreBevels = hasSmallBevels && !hasLargeBevels;
      res.version = "0.4.40";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.40",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      delete res.mainMaskChannel;
      delete res.mainMaskScaleU;
      delete res.mainMaskScaleV;
      delete res.mainMaskTranslateU;
      delete res.mainMaskTranslateV;
      delete res.mainMaskRotation;
      res.version = "0.4.41";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.41",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      if (res.type === "material") {
        res.sdf.common.edgeWearStrength = 1;
      }
      for (const layer of res.layers) {
        layer.maskEdgeWearOverlay = 0;
      }
      res.version = "0.4.42";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.42",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.common.edgeWearSpread = res.sdf.common.edgeWearStrength;
      res.version = "0.4.43";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.43",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      // Trim the end of the patterns list
      while (res.sdf.patterns.length > 0 && !res.sdf.patterns[res.sdf.patterns.length - 1].enabled) {
        res.sdf.patterns.pop();
      }
      res.version = "0.4.44";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.44",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.metalScreen = false;
      res.version = "0.4.45";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.45",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      for (const layer of res.layers) {
        layer.useBaseColorAsColor0 = false;
        layer.useBaseColorAsColor1 = false;
        layer.useBaseColorAsColor2 = false;
        layer.useBaseColorAsColor3 = false;
      }
      res.version = "0.4.46";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.46",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.layerHeightAoStrength = res.selfAoStrength;
      delete res.selfAoStrength;
      res.version = "0.4.47";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.47",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      for (const pattern of res.sdf.patterns) {
        pattern.zSize = res.sdf.common.depth - pattern.zOffset;
        delete pattern.zOffset;
      }
      res.version = "0.4.48";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.48",
    applyOnLayerConfigs: true,
    apply: function (res: any) {
      res.useLegacyGamma = true;
      res.version = "0.4.49";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.49",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.metalScreen = res.metalScreen ? "v1" : "none";
      res.version = "0.4.50";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.50",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      res.sdf.common.rotateAllSdfPatterns = 0;
      res.version = "0.4.51";
      return res;
    },
  },
  {
    maxSchemaVersion: "0.4.51",
    applyOnLayerConfigs: false,
    apply: function (res: any) {
      if (res.metalScreen === "v2") {
        res.transparency = "metalScreen";
      } else if (res.sdf.common.transparent) {
        res.transparency = "sdfTransparent";
      } else if (res.transmission > 0) {
        res.transparency = "experimentalTransmissiveDoNotUse";
      } else if (res.transparent && res.enableApproximateTransmission) {
        res.transparency = "approximateClearGlass";
      } else if (res.transparent) {
        res.transparency = "alphaTransparentDoNotUse";
      } else {
        res.transparency = "opaque";
      }
      // Delete obsolete transparency flags - the transparency mode flag replaces them.
      delete res.sdf.common.transparent;
      delete res.transparent;
      delete res.enableApproximateTransmission;
      delete res.metalScreen;
      res.version = "0.4.52";
      return res;
    },
  },
];
